import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import SearchIcon from '@mui/icons-material/Search';
import Disclaimer from './Disclaimer';
import BrandWordmark from './BrandWordmark';
import PoweredByTrAingle from './PoweredByTrAingle';
import PublicSiteFooter from './PublicSiteFooter';
import { LEGAL_REVIEW_DATE } from './legalDates';
import './InfoPages.css';

const TERMS_SECTIONS = [
  {
    title: 'ARTICLE 1: ACCEPTANCE AND SCOPE',
    items: [
      'These Terms govern access to and use of Redacto services, including web, API, and supported enterprise deployments.',
      'By using the service, you agree to these Terms and all referenced policies.',
    ],
  },
  {
    title: 'ARTICLE 2: SERVICE DESCRIPTION',
    items: [
      'Redacto provides document redaction tooling with human review workflows.',
      'Features and performance may vary by plan, deployment, file type, and product updates.',
      'Product descriptions on marketing pages are informational and do not create guaranteed outcomes unless explicitly stated in a signed agreement.',
    ],
  },
  {
    title: 'ARTICLE 3: CUSTOMER DATA AND PROCESSING',
    items: [
      'Customer retains ownership of uploaded and generated files.',
      'Redacto processes customer content to provide the service and as instructed by the customer.',
      'Files are retained only as needed for processing and review, subject to product settings and contractual terms.',
      'Deletion controls are available in product. Additional account/data deletion requests can be submitted through support channels.',
    ],
  },
  {
    title: 'ARTICLE 4: AI OUTPUT AND HUMAN REVIEW',
    items: [
      'AI-assisted output is probabilistic and may vary by content quality and file type.',
      'Users must review final output before release or external use.',
      'Redacto is designed to remove redacted content from supported export layers; customers are responsible for final verification in their workflow.',
    ],
  },
  {
    title: 'ARTICLE 5: SECURITY AND COMPLIANCE POSITIONING',
    items: [
      'Data is intended to be transmitted over HTTPS/TLS in production deployments, with access controls and audit logging for key workflow events.',
      'References to GDPR, CCPA, SOC 2 readiness, and HIPAA-aware workflows describe alignment goals or supported workflows, not universal legal certification.',
      'PHI workflows require an executed BAA and supported deployment configuration.',
    ],
  },
  {
    title: 'ARTICLE 6: ACCEPTABLE USE',
    items: [
      'Do not use the service for unlawful activity, rights-infringing uploads, malware distribution, harmful automation, or abusive API behavior.',
      'Do not attempt unauthorized access, bypass safeguards, or reverse engineer core service components except as permitted by law.',
      'You are responsible for having rights and legal basis to upload and process document content.',
    ],
  },
  {
    title: 'ARTICLE 7: BILLING, RENEWAL, CANCELLATION, AND REFUNDS',
    items: [
      'If paid plans are enabled in the future, paid plans will be billed as described at purchase or in an order form.',
      'If paid subscriptions are introduced, subscriptions will renew automatically unless canceled before the next renewal date; this does not apply during the current free launch.',
      'Taxes will be your responsibility where applicable if paid features are enabled.',
      'Refund rules, trial conversion behavior, and enterprise invoicing terms will be governed by the Billing and Refund Policy and/or order form when billing is enabled.',
    ],
  },
  {
    title: 'ARTICLE 8: SERVICE AVAILABILITY AND SLA',
    items: [
      'Unless an SLA is expressly included in a signed agreement, service availability is provided on a best-effort basis.',
      'Planned maintenance, security actions, and emergency updates may affect availability.',
    ],
  },
  {
    title: 'ARTICLE 9: EXPORT CONTROLS AND SANCTIONS',
    items: [
      'You must comply with applicable export controls, sanctions laws, and trade restrictions.',
      'You may not use the service if prohibited by applicable sanctions or embargoes.',
    ],
  },
  {
    title: 'ARTICLE 10: INTELLECTUAL PROPERTY',
    items: [
      'Redacto and its licensors own all rights in the service, software, and related materials.',
      'You receive a limited, non-exclusive, non-transferable right to use the service under these Terms.',
    ],
  },
  {
    title: 'ARTICLE 11: TERM, SUSPENSION, AND TERMINATION',
    items: [
      'These Terms remain in effect while you use the service.',
      'Redacto may suspend or terminate access for material breach, security risk, legal compliance needs, or non-payment.',
      'Upon termination, access rights end, and surviving clauses remain effective.',
    ],
  },
  {
    title: 'ARTICLE 12: DISCLAIMERS',
    items: [
      'Except as expressly stated in a signed agreement, the service is provided "as is" and "as available".',
      'Redacto disclaims implied warranties to the extent permitted by law, including merchantability, fitness for a particular purpose, and non-infringement.',
    ],
  },
  {
    title: 'ARTICLE 13: LIMITATION OF LIABILITY',
    items: [
      'To the maximum extent permitted by law, each party\'s aggregate liability under these Terms is limited to fees paid for the service in the three (3) months before the event giving rise to the claim.',
      'Neither party is liable for indirect, incidental, special, consequential, or punitive damages, to the extent permitted by law.',
    ],
  },
  {
    title: 'ARTICLE 14: INDEMNIFICATION',
    items: [
      'You will defend and indemnify Redacto from third-party claims arising from your unlawful use of the service, violation of these Terms, or rights-infringing content uploads.',
    ],
  },
  {
    title: 'ARTICLE 15: GOVERNING LAW, VENUE, AND DISPUTES',
    items: [
      'These Terms are governed by applicable law specified in your order form or, if none is specified, the governing law designated by Redacto at contracting.',
      'Disputes will be resolved in the venue specified in your order form or applicable enterprise agreement.',
      'Where arbitration and class action waiver terms are included in a signed contract, those terms control.',
      'Either party may seek injunctive relief for IP misuse, confidentiality breaches, or security violations.',
    ],
  },
  {
    title: 'ARTICLE 16: ORDER OF PRECEDENCE',
    items: [
      'If terms conflict, the order of precedence is: Order Form, DPA, BAA (if applicable), SLA (if applicable), then these Terms.',
    ],
  },
  {
    title: 'ARTICLE 17: GENERAL TERMS',
    items: [
      'If any part of these Terms is unenforceable, the remainder stays in effect.',
      'Failure to enforce a provision is not a waiver.',
      'Redacto may update these Terms by posting an updated version and revising the Last Updated date.',
      'Contact: info@redacto.net.',
    ],
  },
];

const LAST_UPDATED = LEGAL_REVIEW_DATE;
const WHAT_CHANGED = 'Aligned claims with policy controls and added billing/dispute/compliance contract sections.';
const TERMS_HIGHLIGHTS = [
  {
    label: 'What matters',
    title: 'Clear service boundaries',
    detail: 'Defines what Redacto does, what users must verify, and where enterprise contracts take precedence.',
  },
  {
    label: 'Operational stance',
    title: 'Review-first workflow',
    detail: 'Keeps human verification, deletion controls, and security expectations central to the product.',
  },
  {
    label: 'Commercial path',
    title: 'Enterprise-ready terms',
    detail: 'Covers billing, dispute handling, SLA expectations, and order form precedence without clutter.',
  },
];

const slugify = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');

function Terms({ theme = 'dark', toggleTheme }) {
  const navigate = useNavigate();
  const isDark = theme === 'dark';
  const [isMobile, setIsMobile] = useState(false);
  const [search, setSearch] = useState('');
  const sectionRefs = useRef({});
  const [lastScrollId, setLastScrollId] = useState(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 760px)');
    const handler = (e) => setIsMobile(e.matches);
    handler(mq);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const filteredSections = useMemo(() => {
    if (!search.trim()) return TERMS_SECTIONS;
    const q = search.toLowerCase();
    return TERMS_SECTIONS.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.items.some((item) => item.toLowerCase().includes(q))
    );
  }, [search]);

  useEffect(() => {
    if (!search.trim() || filteredSections.length === 0) return;
    const firstId = slugify(filteredSections[0].title);
    if (firstId === lastScrollId) return;
    const el = sectionRefs.current[firstId];
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setLastScrollId(firstId);
    }
  }, [search, filteredSections, lastScrollId]);

  const highlight = (text) => {
    if (!search.trim()) return text;
    const q = search.toLowerCase();
    const idx = text.toLowerCase().indexOf(q);
    if (idx === -1) return text;
    const before = text.slice(0, idx);
    const match = text.slice(idx, idx + q.length);
    const after = text.slice(idx + q.length);
    return (
      <>
        {before}
        <mark>{match}</mark>
        {after}
      </>
    );
  };

  const handlePrint = () => window.print();

  return (
    <div className={`info-page-shell ${isDark ? 'info-page-dark' : 'info-page-light'}`}>
      <a className="skip-link" href="#terms-content">Skip to content</a>
      <div className="info-page-card">
        <div className="info-page-topbar info-page-topbar-sticky">
          <div className="info-page-brand"><BrandWordmark className="info-page-brand-mark" /><PoweredByTrAingle className="info-page-powered-by" /></div>
          <div className="info-page-topbar-actions">
            {toggleTheme ? (
              <button
                className="theme-toggle-btn info-page-theme-toggle"
                type="button"
                onClick={toggleTheme}
                aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
              >
                {isDark ? <LightModeOutlinedIcon fontSize="small" /> : <DarkModeOutlinedIcon fontSize="small" />}
              </button>
            ) : null}
            <button className="nav-primary-btn" type="button" onClick={() => navigate('/')}>
              Back to Home
            </button>
          </div>
        </div>

        <header className="info-page-header">
          <div className="info-page-badge">Legal</div>
          <h1>Terms of Service</h1>
          <Disclaimer>
            <strong>Beta / Free Tier Disclaimer:</strong> Redacto is currently in a FREE launch / beta phase. Billing and paid subscription features are disabled at present. If and when paid subscription features are introduced, we will update these Terms and provide notice. This launch does not obligate users to any charges.
          </Disclaimer>
          <p>Master Terms of Service Agreement</p>
          <p className="info-page-updated">Last Updated: {LAST_UPDATED}</p>
          <p className="info-page-status-tag">Effective as of {LAST_UPDATED} • What changed: {WHAT_CHANGED}</p>
          <div className="info-page-breadcrumb">
            <button type="button" onClick={() => navigate('/')}>Home</button>
            <span>/</span>
            <span>Terms</span>
          </div>
          <div className="info-page-actions-row">
            <button type="button" className="nav-primary-btn" onClick={handlePrint}>Print-friendly</button>
          </div>
        </header>

        <div id="terms-content" className="info-page-body info-page-body-readable info-page-legal-body">
          <div className="info-page-lead-card">
            <div className="info-page-section-kicker">Legal summary</div>
            <h2>These terms govern access to the Redacto platform and related services.</h2>
            <p>
              They define billing, acceptable use, liability boundaries, dispute handling, and enterprise contract precedence.
            </p>
                
            <div className="info-page-search-box">
              <SearchIcon fontSize="small" />
              <input
                id="terms-search"
                type="search"
                value={search}
                placeholder="Search clauses"
                onChange={(e) => setSearch(e.target.value)}
              />
              {search && (
                <button
                  type="button"
                  className="info-page-search-clear"
                  onClick={() => setSearch('')}
                  aria-label="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <div className="info-page-search-meta">
              {filteredSections.length} section{filteredSections.length !== 1 ? 's' : ''} match{filteredSections.length !== 1 ? '' : 'es'}
            </div>
          </div>

          <div className="info-page-grid info-page-highlight-grid" aria-label="Terms highlights">
            {TERMS_HIGHLIGHTS.map((card) => (
              <article key={card.title} className="info-page-panel info-page-highlight-panel">
                <div className="info-page-section-kicker">{card.label}</div>
                <h3>{card.title}</h3>
                <p>{card.detail}</p>
              </article>
            ))}
          </div>

          <nav className="info-page-toc" aria-label="Terms table of contents">
            <div className="info-page-toc-label">Jump to section</div>
            <ul>
              {filteredSections.length === 0 ? (
                <li className="muted">No sections match that search.</li>
              ) : (
                filteredSections.map((section) => (
                  <li key={section.title}>
                    <a href={`#${slugify(section.title)}`}>{section.title}</a>
                  </li>
                ))
              )}
            </ul>
          </nav>

          {filteredSections.map((section) => {
            const id = slugify(section.title);
            return (
              <details
                key={section.title}
                id={id}
                className="info-page-section info-page-legal-section info-page-accordion"
                open={!isMobile || Boolean(search)}
                ref={(el) => {
                  sectionRefs.current[id] = el;
                }}
              >
                <summary>
                  <span>{section.title}</span>
                  <a className="heading-link" href={`#${id}`} aria-label={`Copy link to ${section.title}`}>#</a>
                </summary>
                {section.items.map((item) => (
                  <p key={item}>{highlight(item)}</p>
                ))}
              </details>
            );
          })}

          <div className="info-page-callout">
            <div className="info-page-callout-title">Questions or enterprise edits?</div>
            <p>Email <a href="mailto:info@redacto.net">info@redacto.net</a>.</p>
          </div>

          <p className="info-page-endcap"><strong>End of Terms of Service Agreement</strong></p>
        </div>
      </div>
      <PublicSiteFooter />
    </div>
  );
}

export default Terms;
